// Visual effects: glitch, scanlines, CRT
// For "The House of Chopras" - Terminal aesthetic with disorientation as feature
import { gsap } from 'gsap';
import { prefersReducedMotion } from './animations';

/**
 * Glitch effect configuration options
 */
export interface GlitchOptions {
  /** Intensity of the glitch effect (1-10, default 5) */
  intensity?: number;
  /** Duration of the complete glitch animation in seconds (default 0.3) */
  duration?: number;
  /** Whether to include color shift effects (default true) */
  colorShift?: boolean;
  /** Whether to include position displacement (default true) */
  displacement?: boolean;
}

/**
 * Apply glitch effect to an element
 * Enhanced with configurable intensity and multiple distortion types
 * @param element - The target HTML element
 * @param options - Configuration options for the glitch effect
 */
export function glitch(element: HTMLElement, options: GlitchOptions = {}): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const {
    intensity = 5,
    duration = 0.3,
    colorShift = true,
    displacement = true,
  } = options;

  const tl = gsap.timeline();
  const skewAmount = intensity * 2;
  const displaceAmount = intensity * 1.5;
  const stepDuration = duration / 4;

  tl.to(element, {
    skewX: skewAmount,
    x: displacement ? displaceAmount : 0,
    duration: stepDuration,
    ease: 'power2.inOut',
  })
    .to(element, {
      skewX: -skewAmount,
      x: displacement ? -displaceAmount : 0,
      filter: colorShift ? 'hue-rotate(90deg)' : 'none',
      duration: stepDuration,
    })
    .to(element, {
      skewX: skewAmount / 2,
      x: displacement ? displaceAmount / 2 : 0,
      filter: colorShift ? 'hue-rotate(-45deg)' : 'none',
      duration: stepDuration,
    })
    .to(element, {
      skewX: 0,
      x: 0,
      filter: 'none',
      duration: stepDuration,
    });

  return tl;
}

/**
 * Apply RGB split/chromatic aberration effect
 * Creates a displaced color channel effect commonly seen in VHS/CRT glitches
 * @param element - The target HTML element
 * @param intensity - Strength of the RGB split (1-10, default 3)
 * @param duration - Duration of the effect in seconds
 */
export function glitchRGBSplit(
  element: HTMLElement,
  intensity: number = 3,
  duration: number = 0.2
): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline();
  const offset = intensity * 2;

  // Apply RGB split using text-shadow for text elements
  const isTextElement = ['P', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV'].includes(element.tagName);

  if (isTextElement) {
    tl.to(element, {
      textShadow: `${offset}px 0 0 rgba(255,0,0,0.7), ${-offset}px 0 0 rgba(0,255,255,0.7)`,
      duration: duration / 2,
      ease: 'steps(2)',
    }).to(element, {
      textShadow: 'none',
      duration: duration / 2,
      ease: 'steps(1)',
    });
  } else {
    // For non-text elements, use filter-based approach
    tl.to(element, {
      filter: `drop-shadow(${offset}px 0 0 rgba(255,0,0,0.5)) drop-shadow(${-offset}px 0 0 rgba(0,255,255,0.5))`,
      duration: duration / 2,
      ease: 'steps(2)',
    }).to(element, {
      filter: 'none',
      duration: duration / 2,
      ease: 'steps(1)',
    });
  }

  return tl;
}

/**
 * Random glitch burst - unpredictable glitch effect for unsettling atmosphere
 * Applies random combination of distortions at random intervals
 * @param element - The target HTML element
 * @param burstCount - Number of glitch bursts (default 3)
 * @param maxIntensity - Maximum intensity for random selection (default 8)
 */
export function glitchBurst(
  element: HTMLElement,
  burstCount: number = 3,
  maxIntensity: number = 8
): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline();

  for (let i = 0; i < burstCount; i++) {
    const randomIntensity = Math.random() * maxIntensity + 1;
    const randomDelay = Math.random() * 0.15;
    const randomDuration = 0.05 + Math.random() * 0.1;

    tl.to(element, {
      skewX: (Math.random() - 0.5) * randomIntensity * 4,
      skewY: (Math.random() - 0.5) * randomIntensity,
      x: (Math.random() - 0.5) * randomIntensity * 3,
      scaleX: 1 + (Math.random() - 0.5) * 0.1,
      filter: Math.random() > 0.5 ? `hue-rotate(${Math.random() * 180}deg)` : 'none',
      duration: randomDuration,
      delay: randomDelay,
      ease: 'steps(1)',
    }).to(element, {
      skewX: 0,
      skewY: 0,
      x: 0,
      scaleX: 1,
      filter: 'none',
      duration: randomDuration,
      ease: 'steps(1)',
    });
  }

  return tl;
}

/**
 * Continuous glitch loop - sustained distortion effect
 * Use for elements that should appear perpetually unstable
 * @param element - The target HTML element
 * @param intensity - Base intensity level (1-10, default 3)
 * @returns Timeline that can be killed to stop the effect
 */
export function glitchLoop(
  element: HTMLElement,
  intensity: number = 3
): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline({ repeat: -1, repeatDelay: Math.random() * 2 + 0.5 });

  // Create several random glitch moments
  for (let i = 0; i < 4; i++) {
    const shouldGlitch = Math.random() > 0.3;
    if (shouldGlitch) {
      tl.to(element, {
        skewX: (Math.random() - 0.5) * intensity * 3,
        x: (Math.random() - 0.5) * intensity * 2,
        filter: `hue-rotate(${Math.random() * 90}deg)`,
        duration: 0.05,
        ease: 'steps(1)',
      }).to(element, {
        skewX: 0,
        x: 0,
        filter: 'none',
        duration: 0.05,
        ease: 'steps(1)',
      });
    }
    // Random pause between glitches
    tl.to({}, { duration: Math.random() * 0.3 + 0.1 });
  }

  return tl;
}

/**
 * Horizontal tear/displacement glitch
 * Simulates horizontal screen tearing commonly seen in video glitches
 * @param element - The target HTML element
 * @param tearCount - Number of tear lines (default 3)
 * @param duration - Duration of the effect
 */
export function glitchTear(
  element: HTMLElement,
  tearCount: number = 3,
  duration: number = 0.4
): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline();
  const stepDuration = duration / (tearCount * 2);

  for (let i = 0; i < tearCount; i++) {
    const tearOffset = (Math.random() - 0.5) * 20;

    tl.to(element, {
      clipPath: `inset(${Math.random() * 30}% 0 ${Math.random() * 30}% 0)`,
      x: tearOffset,
      duration: stepDuration,
      ease: 'steps(1)',
    }).to(element, {
      clipPath: 'inset(0 0 0 0)',
      x: 0,
      duration: stepDuration,
      ease: 'steps(1)',
    });
  }

  return tl;
}

/**
 * Static noise overlay effect
 * Creates a brief static/noise flash like TV signal interference
 * @param element - The target HTML element
 * @param duration - Duration of the static effect
 */
export function glitchStatic(
  element: HTMLElement,
  duration: number = 0.15
): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline();

  // Apply grainy filter effect
  tl.to(element, {
    filter: 'contrast(1.5) brightness(1.2)',
    opacity: 0.9,
    duration: duration / 3,
    ease: 'steps(3)',
  })
    .to(element, {
      filter: 'contrast(0.8) brightness(0.9)',
      opacity: 0.95,
      duration: duration / 3,
      ease: 'steps(2)',
    })
    .to(element, {
      filter: 'none',
      opacity: 1,
      duration: duration / 3,
      ease: 'steps(1)',
    });

  return tl;
}

/**
 * Combine multiple glitch effects for dramatic moments
 * @param element - The target HTML element
 * @param effectTypes - Array of effect types to combine
 */
export function glitchCombo(
  element: HTMLElement,
  effectTypes: Array<'standard' | 'rgb' | 'burst' | 'tear' | 'static'> = ['standard', 'rgb']
): gsap.core.Timeline {
  if (prefersReducedMotion()) {
    return gsap.timeline();
  }

  const tl = gsap.timeline();

  for (const effectType of effectTypes) {
    switch (effectType) {
      case 'standard':
        tl.add(glitch(element, { intensity: 6, duration: 0.2 }));
        break;
      case 'rgb':
        tl.add(glitchRGBSplit(element, 4, 0.15));
        break;
      case 'burst':
        tl.add(glitchBurst(element, 2, 6));
        break;
      case 'tear':
        tl.add(glitchTear(element, 2, 0.2));
        break;
      case 'static':
        tl.add(glitchStatic(element, 0.1));
        break;
    }
  }

  return tl;
}

/**
 * Apply text glitch/corruption effect
 */
export function glitchText(element: HTMLElement, duration: number = 1000): void {
  if (prefersReducedMotion()) return;

  const originalText = element.textContent || '';
  const glitchChars = '█▓▒░╔╗╚╝║═┌┐└┘├┤┬┴┼';
  let iterations = 0;
  const maxIterations = duration / 50;

  const interval = setInterval(() => {
    if (iterations >= maxIterations) {
      clearInterval(interval);
      element.textContent = originalText;
      return;
    }

    element.textContent = originalText
      .split('')
      .map((char) => {
        if (Math.random() < 0.1) {
          return glitchChars[Math.floor(Math.random() * glitchChars.length)];
        }
        return char;
      })
      .join('');

    iterations++;
  }, 50);
}

/**
 * CRT Scanline overlay configuration options
 */
export interface ScanlineOptions {
  /** Intensity of scanlines (0-1, default: 0.15) */
  intensity?: number;
  /** Spacing between scanlines in pixels (default: 2) */
  spacing?: number;
  /** Whether to include animated sweep effect (default: false) */
  animated?: boolean;
  /** Include CRT vignette effect (default: false) */
  vignette?: boolean;
  /** Include subtle flicker effect (default: false) */
  flicker?: boolean;
  /** Use fixed positioning instead of absolute (default: false) */
  fixed?: boolean;
}

// Track injected stylesheets to avoid duplicates
let scanlineKeyframesInjected = false;
let flickerKeyframesInjected = false;

/**
 * Inject CRT sweep animation keyframes into document head
 */
function injectScanlineKeyframes(): void {
  if (scanlineKeyframesInjected) return;

  const style = document.createElement('style');
  style.id = 'crt-scanline-keyframes';
  style.textContent = `
    @keyframes crt-sweep {
      0% { transform: translateY(-100%); }
      100% { transform: translateY(100vh); }
    }
  `;
  document.head.appendChild(style);
  scanlineKeyframesInjected = true;
}

/**
 * Inject CRT flicker animation keyframes into document head
 */
function injectFlickerKeyframes(): void {
  if (flickerKeyframesInjected) return;

  const style = document.createElement('style');
  style.id = 'crt-flicker-keyframes';
  style.textContent = `
    @keyframes crt-flicker-dynamic {
      0% { opacity: 0.97; }
      30% { opacity: 1; }
      60% { opacity: 0.98; }
      100% { opacity: 0.99; }
    }

    .crt-flicker-active {
      animation: crt-flicker-dynamic 0.1s infinite;
    }
  `;
  document.head.appendChild(style);
  flickerKeyframesInjected = true;
}

/**
 * Creates a CRT scanline overlay element
 * This is a global utility for creating consistent CRT effects
 * across the video player and guestbook.
 *
 * @param options - Configuration options for the scanline effect
 * @returns The created overlay element
 */
export function createScanlineOverlay(options: ScanlineOptions = {}): HTMLElement {
  const {
    intensity = 0.15,
    spacing = 2,
    animated = false,
    vignette = false,
    flicker = false,
    fixed = false,
  } = options;

  const overlay = document.createElement('div');
  overlay.className = 'crt-scanline-overlay';
  overlay.setAttribute('aria-hidden', 'true');

  // Base scanline styles
  overlay.style.cssText = `
    position: ${fixed ? 'fixed' : 'absolute'};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      0deg,
      rgba(0, 0, 0, ${intensity}),
      rgba(0, 0, 0, ${intensity}) 1px,
      transparent 1px,
      transparent ${spacing}px
    );
    pointer-events: none;
    z-index: 100;
  `;

  // Add vignette effect
  if (vignette) {
    const vignetteOverlay = document.createElement('div');
    vignetteOverlay.className = 'crt-vignette-overlay';
    vignetteOverlay.setAttribute('aria-hidden', 'true');
    vignetteOverlay.style.cssText = `
      position: ${fixed ? 'fixed' : 'absolute'};
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        ellipse at center,
        transparent 60%,
        rgba(0, 0, 0, 0.4) 100%
      );
      pointer-events: none;
      z-index: 99;
    `;
    overlay.appendChild(vignetteOverlay);
  }

  // Add animated sweep effect
  if (animated && !prefersReducedMotion()) {
    const sweep = document.createElement('div');
    sweep.className = 'crt-sweep';
    sweep.setAttribute('aria-hidden', 'true');
    sweep.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 4px;
      background: rgba(255, 255, 255, 0.03);
      animation: crt-sweep 8s linear infinite;
      pointer-events: none;
    `;
    overlay.appendChild(sweep);

    // Inject keyframes if not already present
    injectScanlineKeyframes();
  }

  // Add flicker effect via class
  if (flicker && !prefersReducedMotion()) {
    overlay.classList.add('crt-flicker-active');
    injectFlickerKeyframes();
  }

  return overlay;
}

/**
 * Add CRT scanline overlay to an element using CSS class
 * Simple version that applies the .crt-effect class
 */
export function addScanlines(element: HTMLElement): void {
  element.classList.add('crt-effect');
}

/**
 * Remove CRT scanline overlay CSS class
 */
export function removeScanlines(element: HTMLElement): void {
  element.classList.remove('crt-effect');
}

/**
 * Apply full CRT scanline overlay to an element
 * Creates a DOM element overlay with configurable options
 *
 * @param container - The container element to add scanlines to
 * @param options - Configuration options
 * @returns Cleanup function to remove the overlay
 */
export function applyScanlineOverlay(
  container: HTMLElement,
  options: ScanlineOptions = {}
): () => void {
  // Ensure container has relative positioning for absolute overlay
  const computedStyle = window.getComputedStyle(container);
  const originalPosition = computedStyle.position;

  if (!options.fixed && originalPosition === 'static') {
    container.style.position = 'relative';
  }

  const overlay = createScanlineOverlay(options);
  container.appendChild(overlay);

  // Return cleanup function
  return () => {
    overlay.remove();
    if (!options.fixed && originalPosition === 'static') {
      container.style.position = '';
    }
  };
}

/**
 * Toggle CRT scanline overlay on an element
 *
 * @param container - The container element
 * @param enabled - Whether to enable or disable the overlay
 * @param options - Configuration options (used when enabling)
 * @returns Cleanup function if enabled, undefined otherwise
 */
export function toggleScanlines(
  container: HTMLElement,
  enabled: boolean,
  options: ScanlineOptions = {}
): (() => void) | undefined {
  // Find and remove existing overlay
  const existingOverlay = container.querySelector('.crt-scanline-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }

  if (enabled) {
    return applyScanlineOverlay(container, options);
  }
  return undefined;
}

/**
 * Apply CRT flicker effect
 */
export function addCRTFlicker(element: HTMLElement): void {
  if (prefersReducedMotion()) return;
  element.classList.add('crt-flicker');
}

/**
 * Remove CRT flicker effect
 */
export function removeCRTFlicker(element: HTMLElement): void {
  element.classList.remove('crt-flicker');
}

/**
 * Configuration for the screen crack effect
 */
export interface ScreenCrackOptions {
  /** Origin point X as percentage (0-100, default: 50) */
  originX?: number;
  /** Origin point Y as percentage (0-100, default: 50) */
  originY?: number;
  /** Number of main crack lines (default: 8) */
  crackCount?: number;
  /** Number of fragments for shatter effect (default: 24) */
  fragmentCount?: number;
  /** Color of cracks - uses CSS variable (default: var(--red)) */
  crackColor?: string;
  /** Delay before callback in ms (default: 0) */
  callbackDelay?: number;
}

/**
 * Create an SVG path for a jagged crack line
 * Generates organic-looking cracks that branch and wobble
 */
function generateCrackPath(
  startX: number,
  startY: number,
  angle: number,
  length: number,
  segments: number = 8
): string {
  let path = `M ${startX} ${startY}`;
  let currentX = startX;
  let currentY = startY;
  const segmentLength = length / segments;

  for (let i = 0; i < segments; i++) {
    // Add randomness to angle for jagged effect
    const wobble = (Math.random() - 0.5) * 0.5; // radians
    const currentAngle = angle + wobble;

    // Segment gets slightly shorter as it goes
    const actualLength = segmentLength * (1 - i * 0.05);

    currentX += Math.cos(currentAngle) * actualLength;
    currentY += Math.sin(currentAngle) * actualLength;

    path += ` L ${currentX} ${currentY}`;
  }

  return path;
}

/**
 * Create a dramatic screen crack transition effect
 * The screen appears to shatter from a central impact point,
 * cracks spread outward, then fragments fall away to reveal the next page.
 */
export function screenCrack(callback: () => void, options: ScreenCrackOptions = {}): void {
  if (prefersReducedMotion()) {
    callback();
    return;
  }

  const {
    originX = 50,
    originY = 50,
    crackCount = 12,
    fragmentCount = 32,
    crackColor = 'var(--red)',
    callbackDelay = 0,
  } = options;

  // Create overlay container
  const overlay = document.createElement('div');
  overlay.className = 'screen-crack-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10000;
    pointer-events: none;
    overflow: hidden;
  `;

  // Create SVG for crack lines
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centerX = (originX / 100) * vw;
  const centerY = (originY / 100) * vh;

  // Create impact flash at center
  const impactFlash = document.createElement('div');
  impactFlash.style.cssText = `
    position: absolute;
    left: ${centerX}px;
    top: ${centerY}px;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(139, 0, 0, 0.8) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    opacity: 0;
  `;
  overlay.appendChild(impactFlash);

  // Create crack lines radiating from center
  const crackPaths: SVGPathElement[] = [];
  for (let i = 0; i < crackCount; i++) {
    const angle = (i / crackCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const length = Math.max(vw, vh) * (0.5 + Math.random() * 0.5);

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const pathData = generateCrackPath(centerX, centerY, angle, length, 12);
    path.setAttribute('d', pathData);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', crackColor);
    path.setAttribute('stroke-width', String(2 + Math.random() * 2));
    path.setAttribute('stroke-linecap', 'round');

    // Set up for drawing animation
    const pathLength = path.getTotalLength ? path.getTotalLength() : length;
    path.style.strokeDasharray = String(pathLength);
    path.style.strokeDashoffset = String(pathLength);
    path.style.filter = 'drop-shadow(0 0 3px rgba(139, 0, 0, 0.5))';

    svg.appendChild(path);
    crackPaths.push(path);

    // Add branch cracks for some lines
    if (Math.random() > 0.5) {
      const branchPoint = 0.3 + Math.random() * 0.4; // 30-70% along the main crack
      const branchX = centerX + Math.cos(angle) * length * branchPoint;
      const branchY = centerY + Math.sin(angle) * length * branchPoint;
      const branchAngle = angle + (Math.random() > 0.5 ? 0.5 : -0.5) + (Math.random() - 0.5) * 0.3;
      const branchLength = length * (0.2 + Math.random() * 0.3);

      const branchPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const branchData = generateCrackPath(branchX, branchY, branchAngle, branchLength, 6);
      branchPath.setAttribute('d', branchData);
      branchPath.setAttribute('fill', 'none');
      branchPath.setAttribute('stroke', crackColor);
      branchPath.setAttribute('stroke-width', String(1 + Math.random()));
      branchPath.setAttribute('stroke-linecap', 'round');

      const branchPathLength = branchPath.getTotalLength ? branchPath.getTotalLength() : branchLength;
      branchPath.style.strokeDasharray = String(branchPathLength);
      branchPath.style.strokeDashoffset = String(branchPathLength);
      branchPath.style.filter = 'drop-shadow(0 0 2px rgba(139, 0, 0, 0.3))';

      svg.appendChild(branchPath);
      crackPaths.push(branchPath);
    }
  }

  overlay.appendChild(svg);

  // Create shatter fragments
  const fragments: HTMLElement[] = [];
  for (let i = 0; i < fragmentCount; i++) {
    const fragment = document.createElement('div');
    const size = 30 + Math.random() * 100;
    const startX = Math.random() * vw;
    const startY = Math.random() * vh;

    // Create triangular/irregular polygon shapes
    const points = [];
    const pointCount = 3 + Math.floor(Math.random() * 3);
    for (let j = 0; j < pointCount; j++) {
      const pAngle = (j / pointCount) * Math.PI * 2;
      const pRadius = (0.3 + Math.random() * 0.7) * (size / 2);
      points.push(`${50 + Math.cos(pAngle) * pRadius}% ${50 + Math.sin(pAngle) * pRadius}%`);
    }

    fragment.style.cssText = `
      position: absolute;
      left: ${startX}px;
      top: ${startY}px;
      width: ${size}px;
      height: ${size}px;
      background: var(--black);
      clip-path: polygon(${points.join(', ')});
      opacity: 0;
      transform-origin: center;
      border: 1px solid ${crackColor};
      box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.8);
    `;

    overlay.appendChild(fragment);
    fragments.push(fragment);
  }

  document.body.appendChild(overlay);

  // Animation timeline
  const tl = gsap.timeline({
    onComplete: () => {
      if (callbackDelay > 0) {
        setTimeout(() => {
          overlay.remove();
          callback();
        }, callbackDelay);
      } else {
        overlay.remove();
        callback();
      }
    },
  });

  // Phase 1: Impact flash
  tl.to(impactFlash, {
    width: 150,
    height: 150,
    opacity: 1,
    duration: 0.08,
    ease: 'power2.out',
  })
    .to(impactFlash, {
      width: 300,
      height: 300,
      opacity: 0,
      duration: 0.15,
      ease: 'power2.out',
    });

  // Phase 2: Cracks spread from center
  tl.to(
    crackPaths,
    {
      strokeDashoffset: 0,
      duration: 0.25,
      stagger: {
        each: 0.02,
        from: 'random',
      },
      ease: 'power2.out',
    },
    '-=0.15'
  );

  // Phase 3: Screen flash white briefly
  tl.to(
    overlay,
    {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      duration: 0.05,
      ease: 'steps(1)',
    },
    '-=0.1'
  )
    .to(overlay, {
      backgroundColor: 'transparent',
      duration: 0.05,
      ease: 'steps(1)',
    });

  // Phase 4: Fragments appear and fall
  tl.to(
    fragments,
    {
      opacity: 1,
      duration: 0.1,
      stagger: {
        each: 0.01,
        from: 'random',
      },
    },
    '-=0.1'
  );

  // Phase 5: Fragments fall down and out
  fragments.forEach((fragment, i) => {
    const fallDirection = Math.random() > 0.5 ? 1 : -1;
    const rotation = (Math.random() - 0.5) * 360;
    const fallDistance = vh + 200;

    tl.to(
      fragment,
      {
        y: fallDistance,
        x: fallDirection * (50 + Math.random() * 150),
        rotation: rotation,
        opacity: 0,
        duration: 0.4 + Math.random() * 0.3,
        ease: 'power2.in',
      },
      `-=${0.35 - i * 0.005}`
    );
  });

  // Phase 6: Cracks fade out
  tl.to(
    crackPaths,
    {
      opacity: 0,
      duration: 0.2,
      stagger: 0.01,
    },
    '-=0.3'
  );
}

/**
 * Apply pixelation transition effect
 */
export function pixelate(element: HTMLElement, callback: () => void): void {
  if (prefersReducedMotion()) {
    callback();
    return;
  }

  const tl = gsap.timeline({
    onComplete: callback,
  });

  tl.to(element, {
    filter: 'blur(10px)',
    opacity: 0,
    duration: 0.3,
    ease: 'power2.in',
  });
}

/**
 * Text mutation effect - subtly changes text when not visible
 * When the element scrolls out of view, the text shifts to a different variant.
 * When it comes back into view, users may notice the text has changed.
 *
 * @param element - The element containing text to mutate
 * @param originalText - The original text content
 * @param variants - Array of alternative text versions to cycle through
 * @returns Cleanup function to disconnect the observer
 */
export function setupTextMutation(
  element: HTMLElement,
  originalText: string,
  variants: string[]
): () => void {
  if (prefersReducedMotion() || variants.length === 0) {
    return () => {};
  }

  // All possible texts: original + variants
  const allTexts = [originalText, ...variants];
  let currentIndex = 0;
  let hasMutated = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Element left the viewport - mutate the text
          currentIndex = (currentIndex + 1) % allTexts.length;
          element.textContent = allTexts[currentIndex];
          hasMutated = true;
        } else if (hasMutated) {
          // Element came back into view after mutation
          // Add animation class to draw subtle attention
          element.classList.add('author-mutable--mutated');

          // Remove the animation class after it completes
          setTimeout(() => {
            element.classList.remove('author-mutable--mutated');
          }, 500);
        }
      });
    },
    {
      // Trigger when element is completely out of view
      threshold: 0,
      // Add some margin so mutation happens just after leaving view
      rootMargin: '-10px',
    }
  );

  observer.observe(element);

  // Return cleanup function
  return () => {
    observer.disconnect();
  };
}

/**
 * Breathing distortion effect - creates subtle warping based on mouse position
 * The effect makes the house feel alive, organic, and slightly unsettling
 */
export interface BreathingEffectConfig {
  /** Maximum skew angle in degrees (default: 0.5) */
  maxSkew?: number;
  /** Maximum scale distortion (default: 0.003) */
  maxScale?: number;
  /** Smoothing factor for mouse movement (0-1, higher = smoother, default: 0.08) */
  smoothing?: number;
  /** Elements to apply individual distortion to (CSS selector) */
  distortTargets?: string;
  /** Maximum perspective shift in pixels (default: 5) */
  maxPerspectiveShift?: number;
}

interface BreathingState {
  mouseX: number;
  mouseY: number;
  targetX: number;
  targetY: number;
  animationFrame: number | null;
  isActive: boolean;
}

/**
 * Setup mouse-movement breathing distortion effect
 * Creates a subtle organic warping that follows the mouse cursor
 */
export function setupBreathingEffect(
  container: HTMLElement,
  config: BreathingEffectConfig = {}
): () => void {
  if (prefersReducedMotion()) {
    return () => {}; // Return empty cleanup function
  }

  const {
    maxSkew = 0.5,
    maxScale = 0.003,
    smoothing = 0.08,
    distortTargets = '.house-corridor, .house-floor-plan, .house-title',
    maxPerspectiveShift = 5,
  } = config;

  const state: BreathingState = {
    mouseX: 0.5,
    mouseY: 0.5,
    targetX: 0.5,
    targetY: 0.5,
    animationFrame: null,
    isActive: true,
  };

  // Get elements to distort
  const targets = container.querySelectorAll<HTMLElement>(distortTargets);

  // Mouse move handler
  const handleMouseMove = (e: MouseEvent) => {
    const rect = container.getBoundingClientRect();
    // Normalize to 0-1 range, centered at 0.5
    state.targetX = (e.clientX - rect.left) / rect.width;
    state.targetY = (e.clientY - rect.top) / rect.height;
  };

  // Mouse leave handler - reset to center
  const handleMouseLeave = () => {
    state.targetX = 0.5;
    state.targetY = 0.5;
  };

  // Animation loop for smooth interpolation
  const animate = () => {
    if (!state.isActive) return;

    // Smooth interpolation towards target
    state.mouseX += (state.targetX - state.mouseX) * smoothing;
    state.mouseY += (state.targetY - state.mouseY) * smoothing;

    // Calculate offset from center (-0.5 to 0.5)
    const offsetX = state.mouseX - 0.5;
    const offsetY = state.mouseY - 0.5;

    // Apply subtle distortion to container
    const containerSkewX = offsetX * maxSkew;
    const containerSkewY = offsetY * maxSkew * 0.5;
    const containerScale = 1 + (Math.abs(offsetX) + Math.abs(offsetY)) * maxScale;

    container.style.transform = `
      perspective(1000px)
      rotateY(${containerSkewX}deg)
      rotateX(${-containerSkewY}deg)
      scale(${containerScale})
    `;

    // Apply individual distortion to targets based on their position
    targets.forEach((target) => {
      const targetRect = target.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate target's center position relative to container
      const targetCenterX = (targetRect.left + targetRect.width / 2 - containerRect.left) / containerRect.width;
      const targetCenterY = (targetRect.top + targetRect.height / 2 - containerRect.top) / containerRect.height;

      // Distance from mouse to this target
      const dx = state.mouseX - targetCenterX;
      const dy = state.mouseY - targetCenterY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Inverse distance effect - closer elements distort more
      const influence = Math.max(0, 1 - distance * 1.5);

      // Apply subtle transform based on proximity
      const targetSkewX = dx * influence * maxSkew * 2;
      const targetSkewY = dy * influence * maxSkew;
      const targetTranslateX = dx * influence * maxPerspectiveShift;
      const targetTranslateY = dy * influence * maxPerspectiveShift;

      target.style.transform = `
        translate(${targetTranslateX}px, ${targetTranslateY}px)
        skewX(${targetSkewX}deg)
        skewY(${targetSkewY}deg)
      `;
    });

    state.animationFrame = requestAnimationFrame(animate);
  };

  // Start listening
  container.addEventListener('mousemove', handleMouseMove);
  container.addEventListener('mouseleave', handleMouseLeave);

  // Start animation loop
  state.animationFrame = requestAnimationFrame(animate);

  // Return cleanup function
  return () => {
    state.isActive = false;
    if (state.animationFrame) {
      cancelAnimationFrame(state.animationFrame);
    }
    container.removeEventListener('mousemove', handleMouseMove);
    container.removeEventListener('mouseleave', handleMouseLeave);

    // Reset transforms
    container.style.transform = '';
    targets.forEach((target) => {
      target.style.transform = '';
    });
  };
}
